<header class="flex w-full flex-col px-4 pb-2 pt-2">
  <div class="flex flex-row items-center gap-4">
    <p-button
      icon="pi pi-arrow-left"
      (onClick)="backToList()"
      [text]="true"
      [rounded]="true"
      pTooltip="Back to list"
    />
    <div class="flex-1">
      <h1 class="text-2xl font-semibold text-greyscale-900">
        {{ editMode ? 'Edit Project' : 'New Project' }}
      </h1>
      @if (projectForm.get('slug')?.value) {
        <p class="font-mono text-sm text-greyscale-500">{{ projectForm.get('slug')?.value }}</p>
      }
    </div>
    @if (editMode && projectForm.get('status')?.value) {
      <span
        class="rounded-full px-3 py-1 text-sm font-medium"
        [ngClass]="getStatusClasses(projectForm.get('status')?.value)"
      >
        {{ projectForm.get('status')?.value }}
      </span>
    }
    <p-button
      [label]="editMode ? 'Update' : 'Create'"
      icon="pi pi-check"
      (onClick)="onSubmit()"
      [disabled]="projectForm.invalid"
    />
  </div>
</header>

<main class="p-4">
  <form [formGroup]="projectForm" (ngSubmit)="onSubmit()">
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-5">

      <!-- LEFT COLUMN -->
      <div class="space-y-4 lg:col-span-3">

        <!-- CONTENT CARD (Multilingual) -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <p-tabs value="0">
            <p-tablist>
              <p-tab value="0">EN</p-tab>
              <p-tab value="1">FR</p-tab>
              <p-tab value="2">NL</p-tab>
            </p-tablist>

            <p-tabpanels>
              <!-- English -->
              <p-tabpanel value="0">
                <div class="space-y-3 pt-2">
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Name *</span>
                    <input pInputText formControlName="nameEN" class="w-full" placeholder="Project name in English" />
                    @if (projectForm.get('nameEN')?.invalid && projectForm.get('nameEN')?.touched) {
                      <small class="text-red-500">Required (3-200 chars)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Short Description *</span>
                    <textarea pTextarea formControlName="shortDescriptionEN" rows="2" class="w-full" placeholder="Brief summary..."></textarea>
                    @if (projectForm.get('shortDescriptionEN')?.invalid && projectForm.get('shortDescriptionEN')?.touched) {
                      <small class="text-red-500">Required (10-500 chars)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Long Description *</span>
                    <textarea pTextarea formControlName="longDescriptionEN" rows="4" class="w-full" placeholder="Detailed description..."></textarea>
                    @if (projectForm.get('longDescriptionEN')?.invalid && projectForm.get('longDescriptionEN')?.touched) {
                      <small class="text-red-500">Required (min 50 chars)</small>
                    }
                  </div>
                  <!-- Optional fields (accordion-style) -->
                  <details class="rounded border border-gray-100 bg-gray-50">
                    <summary class="cursor-pointer px-3 py-2 text-xs font-medium text-gray-500">Optional: Certificate & Social Text</summary>
                    <div class="space-y-3 p-3">
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Certificate Text</span>
                        <textarea pTextarea formControlName="certificateTextEN" rows="2" class="w-full" placeholder="Text for certificates..."></textarea>
                      </div>
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Social Share Text</span>
                        <textarea pTextarea formControlName="socialTextEN" rows="2" class="w-full" placeholder="Text for social sharing..."></textarea>
                      </div>
                    </div>
                  </details>
                </div>
              </p-tabpanel>

              <!-- French -->
              <p-tabpanel value="1">
                <div class="space-y-3 pt-2">
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Nom *</span>
                    <input pInputText formControlName="nameFR" class="w-full" placeholder="Nom du projet en français" />
                    @if (projectForm.get('nameFR')?.invalid && projectForm.get('nameFR')?.touched) {
                      <small class="text-red-500">Requis (3-200 caractères)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Description Courte *</span>
                    <textarea pTextarea formControlName="shortDescriptionFR" rows="2" class="w-full" placeholder="Résumé bref..."></textarea>
                    @if (projectForm.get('shortDescriptionFR')?.invalid && projectForm.get('shortDescriptionFR')?.touched) {
                      <small class="text-red-500">Requis (10-500 caractères)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Description Longue *</span>
                    <textarea pTextarea formControlName="longDescriptionFR" rows="4" class="w-full" placeholder="Description détaillée..."></textarea>
                    @if (projectForm.get('longDescriptionFR')?.invalid && projectForm.get('longDescriptionFR')?.touched) {
                      <small class="text-red-500">Requis (min 50 caractères)</small>
                    }
                  </div>
                  <details class="rounded border border-gray-100 bg-gray-50">
                    <summary class="cursor-pointer px-3 py-2 text-xs font-medium text-gray-500">Optionnel: Texte Certificat & Réseaux Sociaux</summary>
                    <div class="space-y-3 p-3">
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Texte du Certificat</span>
                        <textarea pTextarea formControlName="certificateTextFR" rows="2" class="w-full" placeholder="Texte pour les certificats..."></textarea>
                      </div>
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Texte de Partage</span>
                        <textarea pTextarea formControlName="socialTextFR" rows="2" class="w-full" placeholder="Texte pour le partage..."></textarea>
                      </div>
                    </div>
                  </details>
                </div>
              </p-tabpanel>

              <!-- Dutch -->
              <p-tabpanel value="2">
                <div class="space-y-3 pt-2">
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Naam *</span>
                    <input pInputText formControlName="nameNL" class="w-full" placeholder="Projectnaam in het Nederlands" />
                    @if (projectForm.get('nameNL')?.invalid && projectForm.get('nameNL')?.touched) {
                      <small class="text-red-500">Verplicht (3-200 tekens)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Korte Beschrijving *</span>
                    <textarea pTextarea formControlName="shortDescriptionNL" rows="2" class="w-full" placeholder="Korte samenvatting..."></textarea>
                    @if (projectForm.get('shortDescriptionNL')?.invalid && projectForm.get('shortDescriptionNL')?.touched) {
                      <small class="text-red-500">Verplicht (10-500 tekens)</small>
                    }
                  </div>
                  <div>
                    <span class="mb-1 block text-xs font-medium text-gray-600">Lange Beschrijving *</span>
                    <textarea pTextarea formControlName="longDescriptionNL" rows="4" class="w-full" placeholder="Gedetailleerde beschrijving..."></textarea>
                    @if (projectForm.get('longDescriptionNL')?.invalid && projectForm.get('longDescriptionNL')?.touched) {
                      <small class="text-red-500">Verplicht (min 50 tekens)</small>
                    }
                  </div>
                  <details class="rounded border border-gray-100 bg-gray-50">
                    <summary class="cursor-pointer px-3 py-2 text-xs font-medium text-gray-500">Optioneel: Certificaat & Social Media Tekst</summary>
                    <div class="space-y-3 p-3">
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Certificaat Tekst</span>
                        <textarea pTextarea formControlName="certificateTextNL" rows="2" class="w-full" placeholder="Tekst voor certificaten..."></textarea>
                      </div>
                      <div>
                        <span class="mb-1 block text-xs text-gray-500">Social Media Tekst</span>
                        <textarea pTextarea formControlName="socialTextNL" rows="2" class="w-full" placeholder="Tekst voor delen..."></textarea>
                      </div>
                    </div>
                  </details>
                </div>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        </div>

        <!-- CLASSIFICATION CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Classification</h3>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <span class="mb-1 block text-xs text-gray-500">Type *</span>
              <p-select formControlName="projectType" [options]="typeOptions" placeholder="Select" [style]="{ width: '100%' }" />
            </div>
            <div>
              <span class="mb-1 block text-xs text-gray-500">Scope *</span>
              <p-select formControlName="projectScope" [options]="scopeOptions" placeholder="Select" [style]="{ width: '100%' }" />
            </div>
            <div>
              <span class="mb-1 block text-xs text-gray-500">Region *</span>
              <p-select formControlName="region" [options]="regionOptions" placeholder="Select" [style]="{ width: '100%' }" />
            </div>
            <div>
              <span class="mb-1 block text-xs text-gray-500">Country ISO</span>
              <input pInputText formControlName="country" placeholder="GT" maxlength="2" class="w-full" />
            </div>
            <div class="col-span-2">
              <span class="mb-1 block text-xs text-gray-500">Location</span>
              <input pInputText formControlName="location" placeholder="e.g., Western Highlands, Guatemala" class="w-full" />
            </div>
          </div>
        </div>

        <!-- CERTIFICATION & IMPACT CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Certification & Impact</h3>

          <!-- Certification row -->
          <div class="mb-3 grid grid-cols-3 gap-3">
            <div>
              <span class="mb-1 block text-xs text-gray-500">Standard *</span>
              <p-select formControlName="certificationStandard" [options]="certificationOptions" placeholder="Select" [style]="{ width: '100%' }" />
            </div>
            <div>
              <span class="mb-1 block text-xs text-gray-500">Year</span>
              <p-inputNumber formControlName="certificationYear" [useGrouping]="false" [min]="2000" [max]="2030" [style]="{ width: '100%' }" placeholder="2024" />
            </div>
            <div class="flex items-end pb-2">
              <div class="flex items-center">
                <p-checkbox formControlName="isCertifiedOffset" [binary]="true" inputId="isCertifiedOffset" />
                <label for="isCertifiedOffset" class="ml-2 text-xs">Certified Offset</label>
              </div>
            </div>
          </div>

          <!-- Impact Metrics -->
          @if (getVisibleImpactMetrics().length > 0) {
            <div class="border-t border-gray-100 pt-3">
              <p class="mb-2 text-xs text-gray-400">Impact Metrics (per tonne CO2)</p>
              <div class="grid grid-cols-2 gap-3">
                @if (isMetricVisible('savedTreesPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Saved Trees</span>
                    <p-inputNumber formControlName="savedTreesPerTonne" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('protectedAresPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Protected Land (ares)</span>
                    <p-inputNumber formControlName="protectedAresPerTonne" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('cleanWaterLitersPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Clean Water (L)</span>
                    <p-inputNumber formControlName="cleanWaterLitersPerTonne" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('peopleCleanWaterPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">People with Clean Water</span>
                    <p-inputNumber formControlName="peopleCleanWaterPerTonne" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('cookstovesPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Cookstoves</span>
                    <p-inputNumber formControlName="cookstovesPerTonne" [min]="0" [minFractionDigits]="2" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('householdsSuppliedPerTonne')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Households Supplied</span>
                    <p-inputNumber formControlName="householdsSuppliedPerTonne" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('annualCO2Reduction')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Annual CO2 Reduction (t)</span>
                    <p-inputNumber formControlName="annualCO2Reduction" [min]="0" [minFractionDigits]="2" [style]="{ width: '100%' }" />
                  </div>
                }
                @if (isMetricVisible('annualBeneficiaries')) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500">Annual Beneficiaries</span>
                    <p-inputNumber formControlName="annualBeneficiaries" [min]="0" [style]="{ width: '100%' }" />
                  </div>
                }
              </div>
            </div>
          } @else {
            <p class="text-xs italic text-gray-400">Select a project type to see available metrics</p>
          }
        </div>
      </div>

      <!-- RIGHT COLUMN (Sticky) -->
      <div class="space-y-4 lg:col-span-2 lg:sticky lg:top-4 lg:self-start">

        <!-- SETTINGS CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Settings</h3>

          <div class="mb-3">
            <span class="mb-1 block text-xs text-gray-500">Status</span>
            <p-select formControlName="status" [options]="statusOptions" [style]="{ width: '100%' }" />
          </div>

          <div class="mb-3 flex flex-wrap gap-3">
            <div class="flex items-center">
              <p-checkbox formControlName="isVisible" [binary]="true" inputId="isVisible" />
              <label for="isVisible" class="ml-1.5 text-xs">Visible</label>
            </div>
            <div class="flex items-center">
              <p-checkbox formControlName="isDefault" [binary]="true" inputId="isDefault" />
              <label for="isDefault" class="ml-1.5 text-xs">Default</label>
            </div>
            <div class="flex items-center">
              <p-checkbox formControlName="isFeatured" [binary]="true" inputId="isFeatured" />
              <label for="isFeatured" class="ml-1.5 text-xs">Featured</label>
            </div>
          </div>

          <div class="border-t border-gray-100 pt-3">
            <span class="mb-1 block text-xs text-gray-500">Price per Tonne *</span>
            <p-inputNumber
              formControlName="pricePerTonne"
              mode="currency"
              currency="EUR"
              [minFractionDigits]="2"
              [min]="0"
              [style]="{ width: '100%' }"
            />
            <p class="mt-1 text-xs text-gray-400">B2B partners can override per company</p>
          </div>

          <!-- Odoo Product Integration -->
          @if (odooProductOptions.length > 0) {
            <div class="border-t border-gray-100 pt-3">
              <span class="mb-1 block text-xs text-gray-500">Odoo Product</span>
              <p-select
                formControlName="odooProductId"
                [options]="odooProductOptions"
                placeholder="Select Odoo product..."
                [showClear]="true"
                [filter]="true"
                filterPlaceHolder="Search products..."
                [style]="{ width: '100%' }"
                [loading]="loadingOdooProducts"
                appendTo="body"
              />
              <p class="mt-1 text-xs text-gray-400">Odoo product for invoice generation</p>
            </div>
          }
        </div>

        <!-- DATES CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Dates</h3>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <span class="mb-1 block text-xs text-gray-500">Start</span>
              <p-datepicker formControlName="startDate" dateFormat="yy-mm-dd" [showIcon]="true" [style]="{ width: '100%' }" />
            </div>
            <div>
              <span class="mb-1 block text-xs text-gray-500">Expiration</span>
              <p-datepicker formControlName="expirationDate" dateFormat="yy-mm-dd" [showIcon]="true" [minDate]="today" [style]="{ width: '100%' }" />
            </div>
          </div>
        </div>

        <!-- MEDIA CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Media</h3>

          @if (editMode && project) {
            <div class="grid grid-cols-3 gap-2">
              <!-- Main Image -->
              <div class="text-center">
                <p class="mb-1 text-xs text-gray-500">Main</p>
                @if (mainImageUrl) {
                  <p-image [src]="mainImageUrl" alt="Main" width="80" [preview]="true" styleClass="rounded" />
                } @else {
                  <div class="flex h-16 items-center justify-center rounded bg-gray-100">
                    <i class="pi pi-image text-gray-300"></i>
                  </div>
                }
                @if (uploadingMainImage) {
                  <p-progressSpinner styleClass="w-6 h-6" strokeWidth="4" />
                } @else {
                  <p-fileupload
                    mode="basic"
                    accept="image/*"
                    [maxFileSize]="5000000"
                    (onSelect)="onMainImageSelect($event)"
                    chooseLabel=""
                    chooseIcon="pi pi-upload"
                    styleClass="p-button-sm p-button-text mt-1"
                  />
                }
              </div>

              <!-- Certificate Image -->
              <div class="text-center">
                <p class="mb-1 text-xs text-gray-500">Certificate</p>
                @if (certificateImageUrl) {
                  <p-image [src]="certificateImageUrl" alt="Certificate" width="80" [preview]="true" styleClass="rounded" />
                } @else {
                  <div class="flex h-16 items-center justify-center rounded bg-gray-100">
                    <i class="pi pi-image text-gray-300"></i>
                  </div>
                }
                @if (uploadingCertificateImage) {
                  <p-progressSpinner styleClass="w-6 h-6" strokeWidth="4" />
                } @else {
                  <p-fileupload
                    mode="basic"
                    accept="image/*"
                    [maxFileSize]="5000000"
                    (onSelect)="onCertificateImageSelect($event)"
                    chooseLabel=""
                    chooseIcon="pi pi-upload"
                    styleClass="p-button-sm p-button-text mt-1"
                  />
                }
              </div>

              <!-- Social Image -->
              <div class="text-center">
                <p class="mb-1 text-xs text-gray-500">Social</p>
                @if (socialImageUrl) {
                  <p-image [src]="socialImageUrl" alt="Social" width="80" [preview]="true" styleClass="rounded" />
                } @else {
                  <div class="flex h-16 items-center justify-center rounded bg-gray-100">
                    <i class="pi pi-image text-gray-300"></i>
                  </div>
                }
                @if (uploadingSocialImage) {
                  <p-progressSpinner styleClass="w-6 h-6" strokeWidth="4" />
                } @else {
                  <p-fileupload
                    mode="basic"
                    accept="image/*"
                    [maxFileSize]="5000000"
                    (onSelect)="onSocialImageSelect($event)"
                    chooseLabel=""
                    chooseIcon="pi pi-upload"
                    styleClass="p-button-sm p-button-text mt-1"
                  />
                }
              </div>
            </div>
          } @else {
            <p class="text-center text-xs italic text-gray-400">Save project first to upload images</p>
          }
        </div>

        <!-- CO-BENEFITS CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-2 text-sm font-semibold text-gray-700">Co-Benefits *</h3>
          <p-multiSelect
            formControlName="coBenefits"
            [options]="coBenefitOptions"
            placeholder="Select benefits"
            [style]="{ width: '100%' }"
            display="chip"
          />
        </div>

        <!-- CERTIFICATE CONFIGURATION CARD -->
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <h3 class="mb-3 text-sm font-semibold text-gray-700">Certificate Configuration</h3>

          <!-- Project Icons -->
          <div class="mb-3">
            <span class="mb-1 block text-xs text-gray-500">Project Icons (displayed on certificate)</span>
            <p-multiSelect
              formControlName="projectIcons"
              [options]="projectIconOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select icons"
              [style]="{ width: '100%' }"
              display="chip"
            />
            <p class="mt-1 text-xs text-gray-400">Icons shown on Page 1 & 2 of the certificate</p>
          </div>

          <!-- CO2 Equivalences -->
          <div class="mb-3">
            <span class="mb-1 block text-xs text-gray-500">CO2 Equivalences (Page 2)</span>
            <p-multiSelect
              formControlName="certificateEquivalences"
              [options]="equivalenceOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select equivalences"
              [style]="{ width: '100%' }"
              [selectionLimit]="5"
              display="chip"
            />
            <p class="mt-1 text-xs text-gray-400">Max 5 equivalences for certificate impact page</p>
          </div>

          <!-- Preview Button -->
          @if (editMode) {
            <div class="border-t border-gray-100 pt-3">
              <p-button
                label="Preview Certificate"
                icon="pi pi-eye"
                [outlined]="true"
                (onClick)="openCertificatePreview()"
                [style]="{ width: '100%' }"
              />
            </div>
          }
        </div>

        <!-- PARTNER COMPANIES CARD -->
        @if (editMode) {
          <div class="rounded-lg border border-gray-200 bg-white p-3">
            <h3 class="mb-3 text-sm font-semibold text-gray-700">Partner Companies</h3>
            <p class="mb-3 text-xs text-gray-500">
              Configure which companies have access to this project with custom pricing.
              Only affects companies with "Restricted Projects" enabled.
            </p>

            <!-- Company Multiselect -->
            <div class="mb-3">
              <p-multiSelect
                [options]="allCompanies"
                [(ngModel)]="selectedCompanyIds"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onCompanySelectionChange($event)"
                optionLabel="name"
                optionValue="id"
                placeholder="Select companies..."
                [filter]="true"
                filterPlaceHolder="Search companies..."
                [style]="{ width: '100%' }"
                [loading]="loadingCompanies"
                display="chip"
              />
            </div>

            <!-- Partner Companies Configuration List -->
            @if (loadingPartnerCompanies) {
              <div class="flex justify-center py-4">
                <i class="pi pi-spin pi-spinner text-primary-500"></i>
              </div>
            } @else if (partnerCompanies.length > 0) {
              <!-- Header -->
              <div class="mb-2 grid grid-cols-[1fr_100px_70px] gap-2 px-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                <span>Company</span>
                <span class="flex items-center gap-1">
                  Price/t
                  <i class="pi pi-info-circle cursor-help not-italic"
                     pTooltip="Custom price for this company. Leave empty to use project's default price."
                     tooltipPosition="top"></i>
                </span>
                <span class="flex items-center justify-center gap-1">
                  Default
                  <i class="pi pi-info-circle cursor-help not-italic"
                     pTooltip="Pre-select this project when users from this company create a contribution."
                     tooltipPosition="top"></i>
                </span>
              </div>
              <!-- List -->
              <div class="space-y-1">
                @for (partner of partnerCompanies; track partner.id) {
                  <div class="grid grid-cols-[1fr_100px_70px] items-center gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1.5">
                    <!-- Company name -->
                    <div class="min-w-0">
                      <span class="text-sm font-medium truncate block">{{ partner.company.name }}</span>
                    </div>
                    <!-- Price Override -->
                    <p-inputNumber
                      [(ngModel)]="partner.pricePerTonne"
                      [ngModelOptions]="{standalone: true}"
                      (onBlur)="updatePartnerCompany(partner, 'pricePerTonne', partner.pricePerTonne)"
                      mode="currency"
                      currency="EUR"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="0"
                      placeholder="Default"
                      [inputStyle]="{ width: '100%' }"
                      size="small"
                    />
                    <!-- Default Toggle -->
                    <div class="flex justify-center">
                      <p-checkbox
                        [ngModel]="partner.isDefault"
                        [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="updatePartnerCompany(partner, 'isDefault', $event)"
                        [binary]="true"
                      />
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }

        <!-- DANGER ZONE -->
        @if (editMode) {
          <div class="rounded-lg border border-error-200 bg-error-50 p-3">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-semibold text-error-700">Delete Project</p>
                <p class="text-xs text-error-600">Cannot be undone</p>
              </div>
              <p-button
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                [rounded]="true"
                (onClick)="confirmDelete()"
                [loading]="isDeleting"
              />
            </div>
          </div>
        }
      </div>
    </div>
  </form>
</main>

<p-confirmdialog rejectButtonStyleClass="p-button-outlined" />
<p-toast />

<!-- Certificate Preview Dialog -->
<p-dialog
  [(visible)]="showCertificatePreview"
  header="Certificate Preview"
  [modal]="true"
  [style]="{ width: '90vw', maxWidth: '800px' }"
  [contentStyle]="{ padding: '0', overflow: 'hidden' }"
>
  @if (certificatePreviewUrl) {
    <div class="certificate-preview-wrapper" style="aspect-ratio: 210/297; width: 100%;">
      <iframe
        [src]="certificatePreviewUrl"
        style="width: 100%; height: 100%; border: none;"
        title="Certificate Preview"
      ></iframe>
    </div>
  }
</p-dialog>
